import { useState } from 'react';
import type { ChainId } from '@cryptopay/core';

export interface MasterWalletChangeFormProps {
  readonly onPropose: (
    proposedWallets: Partial<Record<ChainId, string>>,
    proposedBy: string,
  ) => Promise<{ changeId: string; confirmationToken: string }>;
  readonly onConfirm: (changeId: string, token: string) => Promise<void>;
  readonly onCancel: (changeId: string) => Promise<void>;
}

type Mode = 'idle' | 'proposed';

export function MasterWalletChangeForm({
  onPropose,
  onConfirm,
  onCancel,
}: MasterWalletChangeFormProps) {
  const [chainId, setChainId] = useState<ChainId>('polygon');
  const [address, setAddress] = useState('');
  const [proposedBy, setProposedBy] = useState('');
  const [mode, setMode] = useState<Mode>('idle');
  const [changeId, setChangeId] = useState('');
  const [confirmationToken, setConfirmationToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function handlePropose(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      const r = await onPropose({ [chainId]: address }, proposedBy);
      setMode('proposed');
      setChangeId(r.changeId);
      setConfirmationToken(r.confirmationToken);
      setInfo(
        `Önerildi. changeId=${r.changeId}. 24 saat sonra onayla. Token aşağıda görünür.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Propose failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirm(): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      await onConfirm(changeId, confirmationToken);
      setInfo('Master wallet güncellendi.');
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Confirm failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCancel(): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      await onCancel(changeId);
      setInfo('İptal edildi.');
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cancel failed');
    } finally {
      setSubmitting(false);
    }
  }

  function reset(): void {
    setMode('idle');
    setChangeId('');
    setConfirmationToken('');
    setAddress('');
  }

  return (
    <div className="card" aria-label="master-wallet-form">
      <h2 style={{ margin: '0 0 8px' }}>Master Wallet Değişikliği</h2>
      <p className="muted" style={{ marginTop: 0, fontSize: 12 }}>
        24 saat zaman kilidi + email-tarzı confirmation token. Yasal kalkanın
        merkez parçası.
      </p>
      {mode === 'idle' ? (
        <form onSubmit={handlePropose}>
          <label className="muted" style={{ fontSize: 11 }}>CHAIN</label>
          <select
            className="input"
            value={chainId}
            onChange={(e) => setChainId(e.target.value as ChainId)}
            style={{ marginBottom: 8 }}
          >
            <option value="polygon">polygon</option>
            <option value="base">base</option>
            <option value="arbitrum">arbitrum</option>
            <option value="optimism">optimism</option>
            <option value="bsc">bsc</option>
            <option value="avalanche">avalanche</option>
          </select>
          <label className="muted" style={{ fontSize: 11 }}>YENİ ADRES</label>
          <input
            className="input"
            placeholder="0x…"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            required
            style={{ marginBottom: 8 }}
          />
          <label className="muted" style={{ fontSize: 11 }}>PROPOSED BY</label>
          <input
            className="input"
            type="email"
            placeholder="ops@şirketim.com"
            value={proposedBy}
            onChange={(e) => setProposedBy(e.target.value)}
            required
            style={{ marginBottom: 12 }}
          />
          <button type="submit" className="btn" disabled={submitting || !address || !proposedBy}>
            {submitting ? 'Gönderiliyor…' : 'Öneri Gönder'}
          </button>
        </form>
      ) : (
        <div>
          <div className="banner">
            ⏳ Pending change <span className="mono">{changeId}</span>.
            24h time-lock sonrası confirmation token ile uygulayabilir veya iptal edebilirsin.
          </div>
          <label className="muted" style={{ fontSize: 11 }}>CONFIRMATION TOKEN (BİR KEZ GÖRÜNÜR)</label>
          <input
            className="input"
            value={confirmationToken}
            onChange={(e) => setConfirmationToken(e.target.value)}
            style={{ marginBottom: 12 }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={handleConfirm} disabled={submitting}>
              {submitting ? '…' : 'Onayla (24h sonra)'}
            </button>
            <button className="btn danger" onClick={handleCancel} disabled={submitting}>
              İptal Et
            </button>
          </div>
        </div>
      )}
      {info ? <div className="success" style={{ marginTop: 12 }}>{info}</div> : null}
      {error ? <div className="error" style={{ marginTop: 12 }}>{error}</div> : null}
    </div>
  );
}
